//
// casesStore
//
// Stores cases at the town level
//
// refreshAllCases cases=[
// {
//   "csid": "BBmLkou3xjaY6AgBVmDU",
//   "category": "police",
//   "fake": 1,
//   "ctime": "2020-07-08 17:40:53.0",
//   "ltime": "2020-07-08 17:40:57.0",
//   "emergency": null,
//   "lat": null,
//   "lng": null,
// },
//

import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useIsMounted } from '../../components/React';
import {
  caseMatchesStatuses,
  casesStatusParam,
  casesStatusPresetOf,
  casesTeamsParam,
  config,
  fetchTasksList,
  logError,
  partnerLogoUrl,
  useIntervalAtMost,
} from '../../utils';
import useAuth from '../auth/useAuth';
import useCaseRefreshOnPush from '../push/useCaseRefreshOnPush';
import usePush from '../push/usePush';
import usePushEvent from '../push/usePushEvent';

import CasesContext from './CasesContext';
import casesInitialSortingOrder from './casesInitialSortingOrder';
import sortCases from './sortCases';
import sortTasks from './sortTasks';
import tasksSortingOrderFor from './tasksSortingOrderFor';
import useSavedListFilter from './useSavedListFilter';

const pageSize = 100;

const initialState = {
  ready: false,
  allCases: [],
  casesByPiid: { home: [] },
  casesStatusByPiid: { home: 'init' },
  pending: false,
  refreshEnabled: false,
  sortBy: casesInitialSortingOrder,
  loadedSize: pageSize,
  hasMore: false,
  // Clock the standing filter is evaluated on, so a row leaves at a refresh, not mid-view
  refreshedAtMs: 0,
  removedCsid: null,
  // Bumped each time a case is dropped (access lost); the dashboard watches it
  // to deselect the open case. seq makes each removal fire the watcher once.
  removedCsidSeq: 0,
  tasks: [],
  tasksReady: false,
  tasksPending: false,
  tasksEnabled: false,
  taskFilters: null,
  tasksSortBy: tasksSortingOrderFor('tskEndDue', false),
};

const casesKey = (piid) => ((!piid || (piid === 'home')) ? 'home' : piid);

// A case is identified by its csid within its server (home = no piid). Tasks are
// cases, and a partner's task list is fetched from its own server, so they share it.
const caseIdentity = (thisCase) => `${thisCase.piid || ''}:${thisCase.csid}`;

// What a row fetched from a partner server carries so the UI can tell it apart:
// its partnership and the logo standing for it.
const partnerStamp = (partner) => {
  const {
    cuid, piid, logoId, wideLogoId, backUrl,
  } = partner;
  return {
    piid,
    prtnrLogoUrl: partnerLogoUrl(backUrl, cuid, logoId, wideLogoId),
  };
};

// A refresh can answer with a read pointer older than one this client already knows
// about (both fired on the same event): the pointer only ever moves forward
const keepReadPointer = (incoming, previous) => {
  const held = previous?.lastRead;
  if (!held) return incoming;
  if (incoming.lastRead && (new Date(incoming.lastRead) >= new Date(held))) return incoming;

  return {
    ...incoming,
    lastRead: held,
  };
};

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'setRefreshEnabled':
      if (state.refreshEnabled === action.enabled) return state;
      return {
        ...state,
        refreshEnabled: action.enabled,
      };

    case 'refreshStart':
      if (state.pending) return state;
      return {
        ...state,
        pending: true,
      };

    case 'refreshSuccess': {
      // Non-destructive merge: upsert the returned cases (by identity) and keep
      // same-server cases the window did not mention. A full reload is paginated,
      // so absence there is ambiguous (moved to next page vs gone) — removal only
      // happens on the unambiguous single-csid empty fetch (removeCase). piid is
      // undefined for home server cases; other servers are untouched here.
      const {
        cuid, piid, cases, logoId, wideLogoId, backUrl,
      } = action;

      const incoming = (piid)
        ? cases.map((c) => ({
            ...c,
            piid,
            prtnrLogoUrl: partnerLogoUrl(backUrl, cuid, logoId, wideLogoId),
          }))
        : cases;

      const incomingIds = new Set(incoming.map(caseIdentity));
      const retained = state.allCases.filter((c) => {
        const sameServer = (piid) ? (c.piid === piid) : (!c.piid);
        if (!sameServer) return true;
        return !incomingIds.has(caseIdentity(c));
      });

      const held = new Map(state.allCases.map((c) => ([caseIdentity(c), c])));
      const merged = incoming.map((c) => keepReadPointer(c, held.get(caseIdentity(c))));

      return {
        ...state,
        allCases: sortCases([...retained, ...merged], state.sortBy),
        pending: false,
        ready: true,
        refreshedAtMs: Date.now(),
      };
    }

    case 'upsertCase': {
      // One case refreshed via the single-csid fetch: replace or append it.
      const { thisCase } = action;
      const id = caseIdentity(thisCase);
      const previous = state.allCases.find((c) => (caseIdentity(c) === id));
      const without = state.allCases.filter((c) => (caseIdentity(c) !== id));

      return {
        ...state,
        allCases: sortCases([...without, keepReadPointer(thisCase, previous)], state.sortBy),
      };
    }

    case 'markCaseRead': {
      // The fetch answers with the server clock it stamped the read on, so rows catch up
      // now rather than on a refresh. An assigned case is a row in both collections
      const id = `${action.piid || ''}:${action.csid}`;
      const row = state.allCases.find((c) => (caseIdentity(c) === id));
      const readAt = action.readDatim ?? row?.lastModified
                      ?? state.tasks.find((t) => (caseIdentity(t) === id))?.lastModified;
      if (!readAt) return state;

      return {
        ...state,
        allCases: state.allCases.map((c) => ((caseIdentity(c) === id)
          ? keepReadPointer(
              {
                ...c,
                lastRead: readAt,
              },
              c,
            )
          : c)),
        tasks: state.tasks.map((t) => ((caseIdentity(t) === id)
          ? keepReadPointer(
              {
                ...t,
                lastRead: readAt,
              },
              t,
            )
          : t)),
      };
    }

    case 'setTasksEnabled':
      if (state.tasksEnabled === action.enabled) return state;
      return {
        ...state,
        tasksEnabled: action.enabled,
      };

    case 'setTaskFilters':
      return {
        ...state,
        taskFilters: action.filters,
      };

    case 'tasksRefreshStart':
      if (state.tasksPending) return state;
      return {
        ...state,
        tasksPending: true,
      };

    case 'tasksRefreshSuccess': {
      // The task list is a filtered, unpaginated query: one server's answer
      // replaces its own rows whole and leaves the other servers' alone.
      const { piid, tasks } = action;

      const incoming = tasks;
      const retained = state.tasks.filter((t) => {
        const sameServer = (piid) ? (t.piid === piid) : (!t.piid);
        return !sameServer;
      });

      const held = new Map(state.tasks.map((t) => ([caseIdentity(t), t])));

      return {
        ...state,
        tasks: sortTasks(
          [...retained, ...incoming.map((t) => keepReadPointer(t, held.get(caseIdentity(t))))],
          state.tasksSortBy,
        ),
        tasksPending: false,
        tasksReady: true,
      };
    }

    case 'tasksRefreshError':
      if (!state.tasksPending) return state;
      return {
        ...state,
        tasksPending: false,
      };

    case 'upsertTask': {
      // One task refreshed via the single-csid fetch: replace or append it.
      const { thisTask } = action;
      const id = caseIdentity(thisTask);
      const previous = state.tasks.find((t) => (caseIdentity(t) === id));
      const without = state.tasks.filter((t) => (caseIdentity(t) !== id));

      return {
        ...state,
        tasks: sortTasks([...without, keepReadPointer(thisTask, previous)], state.tasksSortBy),
      };
    }

    case 'removeTask': {
      // Single-csid fetch came back empty: the task left the current filters.
      const id = `${action.piid || ''}:${action.csid}`;
      const filtered = state.tasks.filter((t) => (caseIdentity(t) !== id));
      if (filtered.length === state.tasks.length) return state;

      return {
        ...state,
        tasks: filtered,
      };
    }

    case 'removeCase': {
      // Single-csid fetch came back empty: access to this case was lost.
      const id = `${action.piid || ''}:${action.csid}`;
      const filtered = state.allCases.filter((c) => (caseIdentity(c) !== id));
      if (filtered.length === state.allCases.length) return state;

      return {
        ...state,
        allCases: filtered,
        removedCsid: action.csid,
        removedCsidSeq: state.removedCsidSeq + 1,
      };
    }

    case 'refreshError':
      if (!state.pending) return state;
      return {
        ...state,
        pending: false,
      };

    case 'setCasesStatus': {
      const key = casesKey(action.piid);
      return {
        ...state,
        casesStatusByPiid: {
          ...state.casesStatusByPiid,
          [key]: action.value,
        },
      };
    }

    case 'setCases': {
      const key = casesKey(action.piid);
      return {
        ...state,
        casesByPiid: {
          ...state.casesByPiid,
          [key]: action.cases,
        },
        casesStatusByPiid: {
          ...state.casesStatusByPiid,
          [key]: 'ready',
        },
      };
    }

    case 'setTasksSortBy':
      // The list is capped server-side, so the reload that follows decides the rows;
      // the loaded ones are re-ordered meanwhile
      return {
        ...state,
        tasksSortBy: action.sortBy,
        tasks: sortTasks(state.tasks, action.sortBy),
      };

    case 'setSortBy':
      // Re-sort the already-loaded rows now: while WS is up the polling refresh
      // is gated off, so nothing else would re-order the visible list.
      return {
        ...state,
        sortBy: action.sortBy,
        allCases: sortCases(state.allCases, action.sortBy),
      };

    case 'clearHomeCases':
      // Narrowing the team selection must not leave rows of dropped scopes
      // behind; the refresh that follows reloads the window
      return {
        ...state,
        allCases: state.allCases.filter((c) => c.piid),
      };

    case 'loadMore':
      return {
        ...state,
        loadedSize: state.loadedSize + pageSize,
      };

    case 'setHasMore':
      if (state.hasMore === action.value) return state;
      return {
        ...state,
        hasMore: action.value,
      };

    default:
      throw new Error(`casesStore stateReducer unknown action type: ${action.type}`);
  }
};

const refreshPeriodS = 4;
const caseEventDebounceMs = 500;

function CasesProvider(props) {
  const {
    children,
  } = props;
  const { auth } = useAuth();
  const {
    cstmrPost,
    info,
  } = auth;
  const {
    rlid,
    partners,
  } = info ?? {};
  const [searchParams] = useSearchParams();

  // Which teams' cases and which standings the list shows (see useSavedListFilter);
  // the raw strings sit in the deps of the refresh callbacks
  const {
    teams: grpGpids,
    statuses: csStatuses,
    teamsValue: gParam,
    statusesValue: sParam,
    setFilter: setCasesFilter,
  } = useSavedListFilter({
    settingKey: 'casesFilter',
    teamsParam: casesTeamsParam,
    statusesParam: casesStatusParam,
  });
  // The open case rides the same query (?cs=) and stays listed whatever the filter says
  const selectedCsid = searchParams.get('cs');

  const [state, dispatch] = useReducer(stateReducer, initialState);
  const {
    ready,
    allCases,
    pending,
    refreshEnabled,
    sortBy,
    loadedSize,
    hasMore,
    tasks,
    tasksReady,
    tasksPending,
    tasksEnabled,
    taskFilters,
    tasksSortBy,
  } = state;
  const isMountedRef = useIsMounted();
  const { push } = usePush();
  const { wsConnected } = push;

  const clearGrpGpids = useCallback(
    () => setCasesFilter({ teams: [] }),
    [setCasesFilter],
  );

  const refreshCases = useCallback(
    async (piid) => {
      const key = casesKey(piid);
      let error;
      let res;

      dispatch({
        type: 'setCasesStatus',
        piid: key,
        value: 'fetch',
      });
      try {
        const postArgs = {
          urlPath: config.town.back.cases.listAll,
          payload: {
            sortBy,
            page: 0,
            size: loadedSize,
            csStatuses,
          },
        };
        if (key !== 'home') {
          postArgs.piid = piid;
          postArgs.rlid = rlid;
        } else if (grpGpids) postArgs.payload.grpGpids = grpGpids;

        res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!(isMountedRef.current)) return;

      if (!error) {
        let cases = res.body.rslt.data;
        if (cases?.[0]?.outsideHierarchy) {
          // Stale selection (a group moved out of the subtree): reset it
          clearGrpGpids();
          return;
        }
        if (key !== 'home') {
          const partner = partners?.find((p) => (p.piid === piid));
          if (partner) {
            const {
              cuid, logoId, wideLogoId, backUrl,
            } = partner;
            const prtnrLogoUrl = partnerLogoUrl(backUrl, cuid, logoId, wideLogoId);
            cases = cases.map((c) => ({ ...c, piid, prtnrLogoUrl }));
          }
        }
        dispatch({
          type: 'setCases',
          piid: key,
          cases,
        });
        return;
      }
      logError(
        `refreshCases(${key}) Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'CasesProvider.refreshCases.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      dispatch({
        type: 'setCasesStatus',
        piid: key,
        value: 'error',
      });
    },
    [isMountedRef, sortBy, cstmrPost, partners, rlid, loadedSize, grpGpids, clearGrpGpids, csStatuses],
  );

  const refresh = useCallback(
    (arg) => {
      if (typeof arg === 'boolean') {
        dispatch({
          type: 'setRefreshEnabled',
          enabled: arg,
        });
        return;
      }

      // Use void to make it clear we do not want to wait this async function:
      void refreshCases(arg);
    },
    [refreshCases],
  );

  const refreshAllCases = useCallback(
    async (callback) => {
      let error;
      let res;
      dispatch({ type: 'refreshStart' });

      // Fetch home server cases
      try {
        res = await cstmrPost({
          urlPath: config.town.back.cases.listAll,
          payload: {
            sortBy,
            page: 0,
            size: loadedSize,
            csStatuses,
            ...(grpGpids ? { grpGpids } : {}),
          },
        });

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        console.log(`refreshAllCases err=${err}`);
        error = err;
      }

      if (!(isMountedRef.current)) {
        return undefined;
      }

      if (error) {
        logError(
          `refreshAllCases Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'CasesProvider.refreshAllCases.error',
          {
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
          },
        );
        dispatch({ type: 'refreshError' });
        return callback();
      }

      // Home cases have no piid (undefined)
      const homeCases = res.body.rslt.data;
      if (homeCases?.[0]?.outsideHierarchy) {
        // Stale selection (a group moved out of the subtree): reset it; the
        // selection watcher relaunches the refresh
        clearGrpGpids();
        return callback();
      }
      dispatch({
        type: 'refreshSuccess',
        cases: homeCases,
      });

      // A group that returned a full window likely has more rows behind the cap.
      let maxCount = homeCases.length;

      // Fetch partner cases in parallel
      if (partners?.length) {
        const partnerResults = await Promise.all(
          partners.map(async (partner) => {
            const {
              cuid, piid, logoId, wideLogoId, backUrl,
            } = partner;

            try {
              const partnerRes = await cstmrPost({
                piid,
                rlid,
                urlPath: config.town.back.cases.listAll,
                payload: {
                  sortBy,
                  page: 0,
                  size: loadedSize,
                  csStatuses,
                },
              });

              if (partnerRes.status !== 200) {
                console.log(`refreshAllCases partner ${piid} status=${partnerRes.status}`);
                return null;
              }

              return {
                cuid,
                piid,
                cases: partnerRes.body.rslt.data,
                logoId,
                wideLogoId,
                backUrl,
              };
            } catch (err) {
              console.log(`refreshAllCases partner ${piid} err=${err}`);
              return null;
            }
          }),
        );

        if (!(isMountedRef.current)) {
          return undefined;
        }

        partnerResults
          .filter((result) => (result !== null))
          .forEach((result) => {
            maxCount = Math.max(maxCount, result.cases.length);
            dispatch({
              type: 'refreshSuccess',
              ...result,
            });
          });
      }

      dispatch({
        type: 'setHasMore',
        value: (maxCount >= loadedSize),
      });

      return callback();
    },
    [isMountedRef, sortBy, cstmrPost, partners, rlid, loadedSize, grpGpids, clearGrpGpids, csStatuses],
  );

  const refreshAllCasesRef = useRef(refreshAllCases);
  refreshAllCasesRef.current = refreshAllCases;

  // Refresh one case restricting the listAll call. Returns
  //  - one row: upsert
  //  - nothing: the user lost access to this case, remove it from the list.
  const refreshOneCase = useCallback(
    async (csid, piid) => {
      if (!csid) return;
      let error;
      let res;
      try {
        const postArgs = {
          urlPath: config.town.back.cases.listAll,
          payload: {
            sortBy,
            page: 0,
            size: loadedSize,
            csid,
          },
        };
        if (piid) {
          postArgs.piid = piid;
          postArgs.rlid = rlid;
        }
        res = await cstmrPost(postArgs);
        if (res.status !== 200) error = new Error(`Response status=${res.status}`);
      } catch (err) {
        error = err;
      }

      if (!(isMountedRef.current)) return;

      if (error) {
        logError(
          `refreshOneCase(${csid}) Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'CasesProvider.refreshOneCase.error',
          {
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
          },
        );
        return;
      }

      const rows = res.body.rslt.data;
      if (rows.length) {
        let thisCase = rows[0];
        if (piid) {
          const partner = partners?.find((p) => (p.piid === piid));
          if (partner) {
            const {
              cuid, logoId, wideLogoId, backUrl,
            } = partner;
            const prtnrLogoUrl = partnerLogoUrl(backUrl, cuid, logoId, wideLogoId);
            thisCase = { ...thisCase, piid, prtnrLogoUrl };
          }
        }
        dispatch({
          type: 'upsertCase',
          thisCase,
        });
      } else {
        dispatch({
          type: 'removeCase',
          csid,
          piid,
        });
      }
    },
    [isMountedRef, sortBy, cstmrPost, partners, rlid, loadedSize],
  );

  const refreshOneCaseRef = useRef(refreshOneCase);
  refreshOneCaseRef.current = refreshOneCase;

  // A team selection names home groups, which mean nothing on a partner server; the
  // "mine" and "created" filters resolve there from the partner session's own role.
  const partnerTaskFilters = useCallback(
    (query) => {
      const {
        gpids: _gpids,
        ...rest
      } = query;
      return rest;
    },
    [],
  );

  // Loaded only while a view asks for it, so a case event costs no task query otherwise
  const refreshTasks = useCallback(
    async (filters) => {
      const query = filters ?? taskFilters;
      if (!query) return;

      dispatch({ type: 'tasksRefreshStart' });
      try {
        const rows = await fetchTasksList({
          cstmrPost,
          filters: query,
          sortBy: tasksSortBy,
        });
        if (!(isMountedRef.current)) return;
        dispatch({
          type: 'tasksRefreshSuccess',
          tasks: rows,
        });
      } catch (err) {
        logError(
          `refreshTasks Error:${err?.message ?? err}`, // NOSONAR error stringified for logging
          'CasesProvider.refreshTasks.error',
          {
            err: err?.message ?? String(err), // NOSONAR error stringified for logging
            ...(err?.status && { status: err.status }),
          },
        );
        if (isMountedRef.current) dispatch({ type: 'tasksRefreshError' });
      }

      if (!partners?.length) return;

      const partnerQuery = partnerTaskFilters(query);
      await Promise.all(partners.map(async (partner) => {
        const stamp = partnerStamp(partner);
        try {
          const rows = await fetchTasksList({
            cstmrPost,
            filters: partnerQuery,
            sortBy: tasksSortBy,
            piid: partner.piid,
            rlid,
          });
          if (!(isMountedRef.current)) return;
          dispatch({
            type: 'tasksRefreshSuccess',
            piid: partner.piid,
            tasks: rows.map((row) => ({ ...row, ...stamp })),
          });
        } catch (err) {
          logError(
            `refreshTasks partner ${partner.piid} Error:${err?.message ?? err}`, // NOSONAR error stringified for logging
            'CasesProvider.refreshTasks.error',
            {
              err: err?.message ?? String(err), // NOSONAR error stringified for logging
              ...(err?.status && { status: err.status }),
            },
          );
        }
      }));
    },
    [cstmrPost, isMountedRef, taskFilters, tasksSortBy, partners, rlid, partnerTaskFilters],
  );

  // One row re-read under the same filters, so it is added or dropped as it matches them
  const refreshOneTask = useCallback(
    async (csid, piid) => {
      if ((!csid) || (!taskFilters)) return;

      const partner = (piid) ? partners?.find((p) => (p.piid === piid)) : undefined;
      if (piid && (!partner)) return;

      try {
        const query = (piid) ? partnerTaskFilters(taskFilters) : taskFilters;
        const rows = await fetchTasksList({
          cstmrPost,
          filters: { ...query, csid },
          sortBy: tasksSortBy,
          piid,
          rlid,
        });
        if (!(isMountedRef.current)) return;

        if (rows.length) {
          const thisTask = (partner)
            ? {
                ...rows[0],
                ...partnerStamp(partner),
              }
            : rows[0];
          dispatch({
            type: 'upsertTask',
            thisTask,
          });
        } else dispatch({
          type: 'removeTask',
          csid,
          piid,
        });
      } catch (err) {
        logError(
          `refreshOneTask(${csid}) Error:${err?.message ?? err}`, // NOSONAR error stringified for logging
          'CasesProvider.refreshOneTask.error',
          {
            err: err?.message ?? String(err), // NOSONAR error stringified for logging
            ...(err?.status && { status: err.status }),
          },
        );
      }
    },
    [cstmrPost, isMountedRef, taskFilters, tasksSortBy, partners, rlid, partnerTaskFilters],
  );

  const refreshTasksRef = useRef(refreshTasks);
  refreshTasksRef.current = refreshTasks;
  const refreshOneTaskRef = useRef(refreshOneTask);
  refreshOneTaskRef.current = refreshOneTask;
  const tasksEnabledRef = useRef(tasksEnabled);
  tasksEnabledRef.current = tasksEnabled;

  const loadMore = useCallback(
    () => {
      dispatch({ type: 'loadMore' });
    },
    [],
  );

  const didMountRef = useRef(false);
  useEffect(
    () => {
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }
      // Use void to make it clear we do not want to wait this async function:
      void refreshAllCasesRef.current(() => {});
    },
    [loadedSize],
  );

  // Every way the filters can change lands here: the filter panel, a dashboard
  // card link, browser back/forward, a settings read-back, or an outsideHierarchy
  // reset. Drop the rows of scopes no longer selected, then reload the window.
  const filtersMountRef = useRef(false);
  useEffect(
    () => {
      if (!filtersMountRef.current) {
        filtersMountRef.current = true;
        return;
      }
      dispatch({ type: 'clearHomeCases' });
      // Use void to make it clear we do not want to wait this async function:
      void refreshAllCasesRef.current(() => {});
    },
    [gParam, sParam],
  );

  // Polling is the fallback: it runs only while the WS channel is down.
  useIntervalAtMost(
    refreshAllCases,
    (refreshEnabled && (!wsConnected)) ? (1000 * refreshPeriodS) : 0,
    // 'refreshAllCases',
  );

  // First load when enabled, independent of channel: with the poll gated off
  // while WS is up, notifications alone would never populate the initial list.
  useEffect(
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (refreshEnabled) void refreshAllCasesRef.current(() => {});
    },
    [refreshEnabled],
  );

  // On WS (re)connect, reconcile anything missed while the channel was down.
  const wasWsConnectedRef = useRef(false);
  useEffect(
    () => {
      if (refreshEnabled && wsConnected && (!wasWsConnectedRef.current)) {
        // Use void to make it clear we do not want to wait this async function:
        void refreshAllCasesRef.current(() => {});
      }
      wasWsConnectedRef.current = wsConnected;
    },
    [wsConnected, refreshEnabled],
  );
  const partnersRef = useRef(partners);
  partnersRef.current = partners;

  // Case notifications refresh only the target csid. Debounce these events:
  //  - launch a refresh on receiving a first csid notif
  //  - start a refresh block window
  //  - if a new event comes in during this window, do nothing at reception
  //  - at the end of the window, launch a new refresh if we received at least one notif during the window
  const caseEventWindowsRef = useRef(new Map());
  const onCaseEvent = useCallback(
    (data) => {
      const {
        csid,
        cuid: caseCuid,
      } = data ?? {};

      if (!csid) return;

      const piid = (caseCuid)
        ? partnersRef.current?.find((p) => (p.cuid === caseCuid))?.piid
        : undefined;

      const id = `${piid || ''}:${csid}`;

      const windows = caseEventWindowsRef.current;

      const openWindow = () => {
        const timer = setTimeout(
          () => {
            const entry = windows.get(id);
            if (entry?.pending) {
              entry.pending = false;
              // Use void to make it clear we do not want to wait this async function:
              void refreshOneCaseRef.current(csid, piid);
              // Use void to make it clear we do not want to wait this async function:
              if (tasksEnabledRef.current) void refreshOneTaskRef.current(csid, piid);
              openWindow();
            } else {
              windows.delete(id);
            }
          },
          caseEventDebounceMs,
        );
        windows.set(
          id,
          {
            timer,
            pending: false,
          },
        );
      };

      const open = windows.get(id);
      if (open) {
        // Within the throttle window: refresh once at its end.
        open.pending = true;
      } else {
        // Leading edge: refresh now, then block for a window.
        // Use void to make it clear we do not want to wait this async function:
        void refreshOneCaseRef.current(csid, piid);
        // Use void to make it clear we do not want to wait this async function:
        if (tasksEnabledRef.current) void refreshOneTaskRef.current(csid, piid);
        openWindow();
      }
    },
    [],
  );

  usePushEvent('caseSupervised', onCaseEvent);

  useCaseRefreshOnPush(
    onCaseEvent,
    {
      enabled: refreshEnabled,
      includeNewCase: true,
    },
  );

  useEffect(
    () => {
      const windows = caseEventWindowsRef.current;
      return () => {
        windows.forEach((entry) => clearTimeout(entry.timer));
      };
    },
    [],
  );

  // The filters and the order are the query: a new set reloads the list
  useEffect(
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (tasksEnabled && taskFilters) void refreshTasksRef.current();
    },
    [tasksEnabled, taskFilters, tasksSortBy],
  );

  // Picking the current key flips its direction; a new key starts soonest-first
  // for the deadline and newest-first otherwise
  const setTasksSortingOrder = useCallback(
    (newKey) => {
      const current = tasksSortBy[0];
      const desc = (current.key === newKey) ? (!current.desc) : (newKey !== 'tskEndDue');
      dispatch({
        type: 'setTasksSortBy',
        sortBy: tasksSortingOrderFor(newKey, desc),
      });
    },
    [tasksSortBy],
  );

  // Full reload on reconnect catches whatever the push channel missed while down
  const wasWsConnectedForTasksRef = useRef(false);
  useEffect(
    () => {
      if (tasksEnabled && wsConnected && (!wasWsConnectedForTasksRef.current)) {
        // Use void to make it clear we do not want to wait this async function:
        void refreshTasksRef.current();
      }
      wasWsConnectedForTasksRef.current = wsConnected;
    },
    [tasksEnabled, wsConnected],
  );

  const setSortingOrder = useCallback(
    (newKey) => {
      let newSortBy;

      if (sortBy.length) {
        const keyIndex = sortBy.findIndex((sort) => (sort.key === newKey));

        if (keyIndex >= 0) {
          newSortBy = [...sortBy];
          if (keyIndex) {
            const { desc } = sortBy[keyIndex];
            newSortBy.splice(keyIndex);
            newSortBy.splice(
              0,
              0,
              {
                key: newKey,
                desc,
              },
            );
          } else {
            let { desc } = sortBy[0];
            desc = !desc;
            newSortBy[0].desc = desc;
          }
        } else {
          newSortBy = [
            {
              key: newKey,
              desc: true,
            },
            ...sortBy,
          ];
        }
      } else {
        newSortBy = [
          {
            key: newKey,
            desc: true,
          },
        ];
      }

      dispatch({
        type: 'setSortBy',
        sortBy: newSortBy,
      });
    },
    [sortBy],
  );

  const markCaseRead = useCallback(
    (csid, piid, readDatim) => {
      dispatch({
        type: 'markCaseRead',
        csid,
        piid,
        readDatim,
      });
    },
    [],
  );

  const enableTasks = useCallback(
    (enabled) => {
      dispatch({
        type: 'setTasksEnabled',
        enabled,
      });
    },
    [],
  );

  const setTaskFilters = useCallback(
    (filters) => {
      dispatch({
        type: 'setTaskFilters',
        filters,
      });
    },
    [],
  );

  // The server pages on the standing filter; rows kept from earlier pages, and
  // rows whose read grace ran out since, are settled here at each refresh
  const visibleCases = useMemo(
    () => {
      if (casesStatusPresetOf(csStatuses) === 'all') return allCases;
      return allCases.filter((thisCase) => (
        (thisCase.csid === selectedCsid)
        || caseMatchesStatuses(thisCase, csStatuses, state.refreshedAtMs)
      ));
    },
    [allCases, csStatuses, selectedCsid, state.refreshedAtMs],
  );

  const contextValue = useMemo(
    () => ({
      cases: {
        ready,
        sortingOrder: sortBy,
        setSortingOrder,
        grpGpids,
        clearGrpGpids,
        csStatuses,
        setCasesFilter,
        all: visibleCases,
        pending,
        refresh,
        hasMore,
        loadMore,
        markCaseRead,
        removedCsid: state.removedCsid,
        removedCsidSeq: state.removedCsidSeq,
        byPiid: state.casesByPiid,
        status: state.casesStatusByPiid,
      },
      tasks: {
        ready: tasksReady,
        all: tasks,
        pending: tasksPending,
        enable: enableTasks,
        setFilters: setTaskFilters,
        sortingOrder: tasksSortBy,
        setSortingOrder: setTasksSortingOrder,
        refresh: refreshTasks,
      },
    }),
    [
      visibleCases, pending, ready, refresh, setSortingOrder, sortBy, markCaseRead,
      grpGpids, clearGrpGpids, csStatuses, setCasesFilter,
      hasMore, loadMore, state.removedCsid, state.removedCsidSeq,
      state.casesByPiid, state.casesStatusByPiid,
      tasks, tasksReady, tasksPending, enableTasks, setTaskFilters, refreshTasks,
      tasksSortBy, setTasksSortingOrder,
    ],
  );

  return (
    <CasesContext.Provider value={contextValue}>
      {children}
    </CasesContext.Provider>
  );
}

CasesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CasesProvider;
